import React from 'react';
import { Controller } from 'react-hook-form';

function FieldWrapper({ label, helpText, children, error }) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      {children}
      {helpText && <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p>}
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

export default function DynamicField({ field, register, control, error }) {
  const commonClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

  switch (field.fieldType) {
    case 'textarea':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <textarea
            rows={3}
            className={commonClass}
            placeholder={field.placeholder || ''}
            {...register(field.fieldKey)}
          />
        </FieldWrapper>
      );
    case 'checkbox':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <Controller
            name={field.fieldKey}
            control={control}
            render={({ field: input }) => (
              <input
                type="checkbox"
                checked={!!input.value}
                onChange={(e) => input.onChange(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-white/10 rounded"
              />
            )}
          />
        </FieldWrapper>
      );
    case 'select':
    case 'radio':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <select className={commonClass} {...register(field.fieldKey)}>
            <option value="">Select…</option>
            {(field.options || []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </FieldWrapper>
      );
    case 'multiselect':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <Controller
            name={field.fieldKey}
            control={control}
            render={({ field: input }) => (
              <select
                multiple
                className={commonClass}
                value={input.value || []}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions).map((o) => o.value);
                  input.onChange(values);
                }}
              >
                {(field.options || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
          />
        </FieldWrapper>
      );
    case 'date':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <input type="date" className={commonClass} {...register(field.fieldKey)} />
        </FieldWrapper>
      );
    case 'number':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <input type="number" className={commonClass} placeholder={field.placeholder || ''} {...register(field.fieldKey)} />
        </FieldWrapper>
      );
    case 'email':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <input type="email" className={commonClass} placeholder={field.placeholder || ''} {...register(field.fieldKey)} />
        </FieldWrapper>
      );
    case 'password':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <input type="password" className={commonClass} placeholder={field.placeholder || ''} {...register(field.fieldKey)} />
        </FieldWrapper>
      );
    case 'file':
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <Controller
            name={field.fieldKey}
            control={control}
            render={({ field: input }) => (
              <input
                type="file"
                className={commonClass}
                onChange={(e) => input.onChange(e.target.files?.[0] || null)}
              />
            )}
          />
        </FieldWrapper>
      );
    case 'text':
    default:
      return (
        <FieldWrapper label={field.label} helpText={field.helpText} error={error}>
          <input type="text" className={commonClass} placeholder={field.placeholder || ''} {...register(field.fieldKey)} />
        </FieldWrapper>
      );
  }
}


